/**
 * Every hex colour used across this project's charts, in one place. Edit here
 * to change a colour anywhere it appears — `crosswalk.ts` (current-campaign
 * party colours, used by PollChart) and `HistoricalElections/party-meta.js`
 * (political-bloc colours, used by ElectionFlow) both read from this file
 * instead of hardcoding their own hexes.
 */

/**
 * Reuters Baselite categorical palette:
 * https://reuters-graphics.github.io/style/colors/
 */
export const baselite = {
  yellowLite: '#ffd430',
  brownLite: '#948072',
  redLite: '#ee665b',
  orangeLite: '#f68e26',
  blueLite: '#569ed3',
  cyanLite: '#4ec7ff',
  grayLite: '#cccccc',
  purpleLite: '#9582c1',
  whiteLite: '#f9f9f9',
};

// Darker navy from Reuters' "Blueberry" sequential scheme — the Baselite
// categorical set above has no second blue, so this is borrowed to give
// Far-right a shade darker than Likud's blueLite.
export const blueDark = '#044F74';

/**
 * Brand palette (2026 refresh) — six-colour categorical series for the main
 * political blocs. Named per the brand spec (`--gfx-*`), in series order.
 */
export const gfx = {
  rust: '#ba5029', // --gfx-rust — primary, first series in any chart
  blue: '#2c598e', // --gfx-blue — second series
  teal: '#4596a0', // --gfx-teal — third series
  plum: '#43193f', // --gfx-plum — fourth series
  green: '#104931', // --gfx-green — fifth series
  yellow: '#d19d2e', // --gfx-yellow — sixth series, fill only
};


// export const blocColors = {
//   arab: gfx.yellow,
//   left: gfx.rust,
//   centre: gfx.green,
//   // Poll-chart-only shade for Israel Our Home/Reservists/People of Israel's
//   // line colours (via `crosswalk.ts`'s `partyBlocs`) — unified with
//   // `rightBloc` below onto the same brand teal (previously kept distinct so
//   // those lines wouldn't share Likud's exact colour; Likud has its own
//   // `likud` token below regardless, so no collision).
//   right: gfx.teal,
//   likud: baselite.blueLite,
//   farRight: gfx.blue,
//   nationalReligious: baselite.grayLite,
//   ultraOrthodox: gfx.plum,
//   // Catch-all for any party not covered by a curated bloc (e.g. a
//   // current-campaign party like Reservists with no confirmed real-world
//   // bloc yet, see `crosswalk.ts`/`HistoricalElections/party-meta.js`'s
//   // `comparisonDefinitions`). Was `baselite.whiteLite` (#f9f9f9), which read
//   // as blank against the page's white background — needs to actually be
//   // visible since it can appear as a real legend chip/bar segment, not just
//   // a chart-filler like `unassigned` below.
//   other: '#767676',
//   // Not a real political bloc — a light-gray filler `ElectionFlow` draws for
//   // any seats an estimate row can't attribute to a party (see `unassigned`
//   // in `HistoricalElections/party-meta.js`'s `blocMeta`).
//   unassigned: '#e0e0e0',
//   // The "Right" bloc bar/legend colour shared by BOTH `ElectionFlow`-based
//   // charts: the standalone Historical Elections timeline, which merges
//   // right/likud/far-right/national-religious into one "Right wing parties"
//   // bloc, and `ElectionComparison`'s `comparisonBlocMeta`, whose narrower
//   // "Right" bucket (Likud/far-right/national-religious kept separate) would
//   // otherwise read a different blue for the same "Right" legend label. Edit
//   // this one value to recolour "Right" everywhere it appears as a bloc.
//   rightBloc: gfx.teal,
// };

// ── Previous palette (pre brand refresh) — kept for easy rollback ──────────
export const blocColors = {
  arab: baselite.yellowLite,
  left: baselite.redLite, // was baselite.brownLite (#948072)
  centre: baselite.orangeLite, // was baselite.redLite (#ee665b)
  right: baselite.cyanLite, // was baselite.orangeLite (#f68e26)
  likud: baselite.blueLite,
  farRight: blueDark, // was baselite.cyanLite (#4ec7ff)
  nationalReligious: baselite.grayLite,
  ultraOrthodox: baselite.purpleLite,
  other: baselite.whiteLite,
  unassigned: '#e0e0e0',
  rightBloc: baselite.blueLite,
};

/**
 * Mixes `hex` toward white by `amount` (0 = unchanged, 1 = white). Used to
 * give parties that share a bloc colour in `crosswalk.ts` a muted variant
 * instead of an identical swatch.
 */
// export const lighten = (hex: string, amount: number): string => {
//   const n = parseInt(hex.slice(1), 16);
//   const mix = (channel: number) =>
//     Math.round(channel + (255 - channel) * amount)
//       .toString(16)
//       .padStart(2, '0');
//   return `#${mix((n >> 16) & 255)}${mix((n >> 8) & 255)}${mix(n & 255)}`;
// };

/**
 * Returns the original color for the first variant,
 * then progressively darker variants.
 *
 * index = 0 → original color
 * index = 1 → darker
 * index = 2 → even darker
 */
export const darken = (hex: string, index: number): string => {
  // First party keeps the exact original color
  if (index === 0) return hex;

  const n = parseInt(hex.replace('#', ''), 16);

  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;

  // Adjust this to control the difference between variants
  const strength = Math.min(index * 0.18, 0.65);

  const channel = (value: number) =>
    Math.round(value * (1 - strength))
      .toString(16)
      .padStart(2, '0');

  return `#${channel(r)}${channel(g)}${channel(b)}`;
};
